import { memo, type ReactNode, useCallback, useMemo } from 'react';

import { StarIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';

import { DragType } from '~/components/Shared/Drag/dragTypes';
import { useScrollElementContext } from '~/components/v2/components/shared/scroll/ScrollElementContextProvider';
import { SubSideNavSectionHeader } from '~/components/v2/components/workspace/sub-side-nav/SubSideNavSectionHeader';
import {
  WorkspaceSidebarBoardsList,
  type WorkspaceSidebarBoardsListProps,
} from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsList';
import { WorkspaceSidebarBoardsListItemSkeleton } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsListItemSkeleton';
import { WorkspaceSidebarCollapsible } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarCollapsible';
import { WorkspaceSidebarSection } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSection';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { useFavoriteBoards } from '~/components/v2/hooks/useFavoriteBoards';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useRearrangeBoards } from '~/swr-hooks/gallery/useGalleryRearrange';

const SUPPORTED_BOARD_DRAG_TYPES = [DragType.boardNavWorkspace, DragType.boardNavLibrary, DragType.boardCard];

export type WorkspaceSidebarFavoriteBoardsProps = {
  leadingItems?: ReactNode;
};

export const WorkspaceSidebarFavoriteBoards = memo(({ leadingItems }: WorkspaceSidebarFavoriteBoardsProps) => {
  const { scrollElement } = useScrollElementContext();
  const {
    data: boards,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingBoards,
  } = useFavoriteBoards();
  const collapsed = useWorkspaceSidebarSectionStore((store) => store.collapsed);
  const toggleCollapsed = useWorkspaceSidebarSectionStore((store) => store.toggleCollapsed);
  const { rearrangeFavoriteBoards } = useRearrangeBoards();

  const flatBoards = useMemo(() => {
    return boards?.pages.flatMap((page) => page.data) ?? [];
  }, [boards]);

  useFetchObjectsPermissions({
    objects: {
      boardIds: flatBoards.map((board) => board.id),
    },
  });

  const onBoardRearrange: WorkspaceSidebarBoardsListProps['onBoardRearrange'] = useCallback(
    ({ adjacentItem, boards, parentBoardId, board }) =>
      rearrangeFavoriteBoards({
        boards,
        draggedItem: board,
        adjacentItem,
        parentBoardId,
      }),
    [rearrangeFavoriteBoards],
  );

  const getCanRearrange: WorkspaceSidebarBoardsListProps['getCanRearrange'] = useCallback(() => true, []);

  const handleLoadNextPage = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const header = useMemo(
    () => (
      <SubSideNavSectionHeader
        collapsed={collapsed}
        data-testid="LIBRARIES_SUB_SIDE_NAV_FAVORITE_BOARDS_HEADER"
        icon={StarIcon}
        label="Favorites"
        onToggle={toggleCollapsed}
      />
    ),
    [collapsed, toggleCollapsed],
  );

  const boardsContent = useMemo(() => {
    if (isLoadingBoards) {
      return (
        <>
          {leadingItems}
          <WorkspaceSidebarBoardsListItemSkeleton />
          <WorkspaceSidebarBoardsListItemSkeleton />
        </>
      );
    }

    return (
      <>
        {leadingItems}
        <WorkspaceSidebarBoardsList
          boards={flatBoards}
          depth={0}
          dragType={DragType.boardNavFavorites}
          draggable={true}
          droppable={true}
          getCanRearrange={getCanRearrange}
          onBoardRearrange={onBoardRearrange}
          parentBoardId={null}
          scrollElement={scrollElement}
          supportedBoardDragTypes={SUPPORTED_BOARD_DRAG_TYPES}
        />
      </>
    );
  }, [flatBoards, getCanRearrange, isLoadingBoards, leadingItems, onBoardRearrange, scrollElement]);

  return (
    <WorkspaceSidebarSection data-testid="WORKSPACE_SIDEBAR_FAVORITE_BOARDS">
      {header}
      <WorkspaceSidebarCollapsible open={!collapsed}>
        {boardsContent}
        {hasNextPage && !isLoadingBoards ? (
          <Button
            appearance="ghost"
            className="ml-9 justify-start"
            color="gray"
            data-testid="WORKSPACE_SIDEBAR_FAVORITE_BOARDS_SHOW_MORE"
            isLoading={isFetchingNextPage}
            onClick={handleLoadNextPage}
            size="small"
          >
            Show more
          </Button>
        ) : null}
      </WorkspaceSidebarCollapsible>
    </WorkspaceSidebarSection>
  );
});

WorkspaceSidebarFavoriteBoards.displayName = 'WorkspaceSidebarFavoriteBoards';
